import { APP_NAME } from '@/constants';
import { create } from 'zustand';
import { createGlobalStoreActions } from './actions';
import { devtools, persist } from 'zustand/middleware';
import type { GlobalState, GlobalStateActions } from './types';

/**
 * Global Store Interface
 *
 * Combines the global state and actions into a single interface for use with Zustand.
 * This defines the shape of the store and ensures type safety when accessing state and actions.
 */
export interface GlobalStore extends GlobalState, GlobalStateActions {}

/**
 * Unique name for the global store, used for persistence in localStorage.
 * This ensures that the store's state is saved and can be rehydrated across sessions.
 */
export const STORE_NAME = `${APP_NAME}-Store`;

/**
 * Initial state of the global store, defining default values for all state properties.
 * This ensures that the store has a consistent structure and default values when the app loads.
 */
export const initialGlobalStoreState: GlobalState = {
  activeCertificate: null,
  activeCertification: null,
  activePersona: null,
  activeScore: null,
  activeSessionId: null,
  activeSimulation: null,
  activeUser: null,
  appConfig: null,
  certifications: [],
  certificationsError: null,
  currentUser: null,
  currentUserBadges: [],
  error: null,
  fetchData: true,
  fetchManagerData: true,
  isLoading: true,
  isPrinting: false,
  managerActiveProduct: null,
  managerActiveUser: null,
  managerActiveUserId: null,
  managerActiveUserIsLoading: true,
  managerActiveUserScorecardIsLoading: true,
  managersTeamSummary: null,
  navExpanded: false,
  notifications: [],
  personas: [],
  rubrics: [],
  scores: [],
  showOutageAlert: false,
  sidebarCertificate: null,
  sidebarScorecard: null,
  simulations: [],
  usageConsentAccepted: true,
  apiHealth: null,
};

/**
 * Global Store
 *
 * This is the main Zustand store that holds the global state and actions for the application.
 * It uses the devtools and persist middleware to enable debugging and state persistence.
 */
export const useGlobalStore = create<GlobalStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialGlobalStoreState,
        ...createGlobalStoreActions(set, get),
      }),
      {
        name: STORE_NAME,
        // Only persist certain parts of the state
        partialize: (state) => ({
          activeSessionId: state.activeSessionId,
          appConfig: state.appConfig,
          currentUser: state.currentUser,
        }),
      },
    ),
    {
      name: STORE_NAME,
    },
  ),
);

// Selectors for common use cases
export const useAppConfig = () => useGlobalStore((state) => state.appConfig);
export const useCurrentUser = () => useGlobalStore((state) => state.currentUser);
export const useError = () => useGlobalStore((state) => state.error);
export const usePersonas = () => useGlobalStore((state) => state.personas);
export const useRubrics = () => useGlobalStore((state) => state.rubrics);

/**
 * Custom hook to access certifications from the global store.
 * This provides a convenient way to retrieve certifications and any related error state.
 */
export const useStoreCertifications = () => {
  return useGlobalStore((state) => ({
    activeCertification: state.activeCertification,
    certifications: state.certifications,
    certificationsError: state.certificationsError,
    setActiveCertification: state.setActiveCertification,
    setCertifications: state.setCertifications,
    setCertificationsError: state.setCertificationsError,
  }));
};

/**
 * Custom hook to access simulations from the global store.
 * This provides a convenient way to manage the active simulation and the list of simulations in the store.
 */
export const useStoreSimulations = () => {
  return useGlobalStore((state) => ({
    activeSimulation: state.activeSimulation,
    simulations: state.simulations,
    setActiveSimulation: state.setActiveSimulation,
    setSimulations: state.setSimulations,
  }));
};

/**
 * Custom hook to access user-related state and actions from the global store.
 * This provides a convenient way to update user information and manage users in the store.
 */
export const useStoreUsers = () => {
  return useGlobalStore((state) => ({
    currentUser: state.currentUser,
    setCurrentUser: state.setCurrentUser,
  }));
};

/**
 * Custom hook to access manager-related state and actions from the global store.
 * This provides a convenient way to manage the active user in the manager's view and related loading states.
 */
export const useStoreManager = () => {
  return useGlobalStore((state) => ({
    managerActiveUser: state.managerActiveUser,
    managerActiveUserId: state.managerActiveUserId,
    managerActiveUserIsLoading: state.managerActiveUserIsLoading,
    managerActiveUserScorecardIsLoading: state.managerActiveUserScorecardIsLoading,
    managersTeamSummary: state.managersTeamSummary,
    setManagerActiveUser: state.setManagerActiveUser,
    setManagerActiveUserId: state.setManagerActiveUserId,
    setManagerActiveUserIsLoading: state.setManagerActiveUserIsLoading,
    setManagerActiveUserScorecardIsLoading: state.setManagerActiveUserScorecardIsLoading,
    setManagersTeamSummary: state.setManagersTeamSummary,
    updateManagerActiveUser: state.updateManagerActiveUser,
  }));
};

/**
 * Custom hook to access notification actions from the global store.
 * Provides helpers for managing the notifications stream.
 */
export const useStoreNotifications = () => {
  return useGlobalStore((state) => ({
    notifications: state.notifications,
    addNotification: state.addNotification,
    addNotifications: state.addNotifications,
    clearNotifications: state.clearNotifications,
    markAllNotificationsRead: state.markAllNotificationsRead,
    markNotificationRead: state.markNotificationRead,
    removeNotification: state.removeNotification,
    setNotifications: state.setNotifications,
  }));
};
